import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Lock, Loader2 } from 'lucide-react';
import { apiFetch } from '../fetchInterceptor';

/**
 * TabLockedToast — global listener for the `cb:tab-locked` window event
 * dispatched by fetchInterceptor when the backend returns a 403 with
 * `{detail:{error:'tab_disabled', tab_id, message}}`.
 *
 * Renders a sonner toast that includes a "Request access" action which
 * fires POST /api/access/request with the current workspace_id + tab_id.
 * After a successful request the toast updates to confirm.
 *
 * Reads `currentWorkspaceId` from localStorage (set by WorkspaceContext)
 * instead of useWorkspace() so it can mount at the App root — outside any
 * WorkspaceProvider — and still know which workspace to ping.
 *
 * Mount once at the app shell (App.jsx). Zero render output.
 */
export default function TabLockedToast() {
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const handler = async (e) => {
      const { tab_id: tabId, message } = e.detail || {};
      if (!tabId) return;

      const backend = process.env.REACT_APP_BACKEND_URL;
      let wsId = null;
      try { wsId = localStorage.getItem('currentWorkspaceId'); } catch { /* iframe */ }

      // Without a workspace_id we can't POST a request, so fall back to a
      // plain error toast.
      if (!wsId || !backend) {
        toast.error(message || `Access to ${tabId} has been disabled by your workspace admin.`, {
          description: 'Ask an admin to enable this area in Settings → Team → Access overrides.',
          duration: 6000,
        });
        return;
      }

      const toastId = `tab-locked-${tabId}`;
      toast.error(message || `Access to ${tabId} has been disabled.`, {
        id: toastId,
        description: 'Click below to ping your workspace admins.',
        duration: 10000,
        action: {
          label: pending ? 'Requesting…' : 'Request access',
          onClick: async () => {
            setPending(true);
            try {
              const resp = await apiFetch(`${backend}/api/access/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ workspace_id: wsId, tab_id: tabId }),
              });
              const data = await resp.json().catch(() => ({}));
              if (!resp.ok) {
                toast.error(data?.detail || 'Could not send request', { id: toastId });
                return;
              }
              toast.success(
                data?.already_pending
                  ? 'You already have a pending request — admins were re-notified.'
                  : 'Request sent. An admin will review it shortly.',
                {
                  id: toastId,
                  description: `Tab: ${tabId}`,
                  duration: 6000,
                },
              );
            } catch {
              toast.error('Network error. Try again in a moment.', { id: toastId });
            } finally {
              setPending(false);
            }
          },
        },
        icon: <Lock className="h-4 w-4" />,
      });
    };

    window.addEventListener('cb:tab-locked', handler);
    return () => window.removeEventListener('cb:tab-locked', handler);
  }, [pending]);

  return null;
}

// Tiny exported helper for any non-toast surface (e.g. a full-page lock
// screen) that wants to fire the same request flow without re-implementing.
export async function requestTabAccess({ workspaceId, tabId, reason }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const resp = await apiFetch(`${backend}/api/access/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ workspace_id: workspaceId, tab_id: tabId, reason }),
  });
  return resp.json();
}

// Suppress unused-import warning for the loader (kept around for future
// "Requesting…" spinner UX in the toast action).
void Loader2;
